import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, Clock, ChevronDown } from 'lucide-react';
import { siteContent } from '../data/content';
import { healthPackagesData } from '../data/healthPackagesData';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top Bar */}
      <div className="bg-green-800 text-white py-2 px-4">
        <div className="container mx-auto flex justify-between items-center text-sm flex-wrap gap-2">
        <div className="flex items-center gap-4 flex-wrap">
  {siteContent.branding.phones.map((phone, index) => (
    <a
      key={index}
      href={`tel:${phone}`}
      className="flex items-center gap-1 hover:text-green-600 transition-colors"
    >
      <Phone size={16} />
      <span>{phone}</span>
    </a>
  ))}

  <a
    href={`mailto:${siteContent.branding.email}`}
    className="flex items-center gap-2 hover:text-green-600 transition-colors"
  >
    <Mail size={16} />
    <span className="hidden sm:inline">
      {siteContent.branding.email}
    </span>
  </a>
</div>
          <div className="flex items-center gap-2">
            <Clock size={16} />
            <span className="hidden sm:inline">Mon-Sat: 8AM-10PM | Sun: 8AM-3PM</span>
            <span className="sm:hidden">24/7 Emergency</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass-effect shadow-lg' : 'bg-white'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
 
  <div
    className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform overflow-hidden"
    style={{ backgroundColor: 'rgba(0, 76, 54, 1)' }}
  >
    <img
      src={process.env.PUBLIC_URL + "/images/logo.png"}
      alt="Logo"
      className="w-full h-full object-contain"
    />
  </div>
  <div>
    <h1 className="text-xl font-bold text-black-900 group-hover:text-black-800 transition-colors">
      {siteContent.branding.name}
    </h1>
    <p className="text-black text-black-400">{siteContent.branding.tagline}</p>
  </div>
</Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              <Link
                to="/"
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  isActive('/') ? 'text-black-800 bg-orange-50' : 'text-black-700 hover:text-orange-800 hover:bg-orange-50'
                }`}
                data-testid="nav-home"
              >
                Home
              </Link>

              {/* Services Dropdown */}
              <NavigationMenu>
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <NavigationMenuTrigger
                      className={`font-medium ${
                        location.pathname.startsWith('/services') ? 'text-orange-800 bg-orange-50' : 'text-black-700'
                      }`}
                      data-testid="nav-services"
                    >
                      Services
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="w-64 p-4">
                        {siteContent.services.map((service) => (
                          <Link
                            key={service.id}
                            to={`/services/${service.id}`}
                            className="block px-4 py-3 rounded-lg hover:bg-orange-50 text-gray-700 hover:text-orange-800 transition-colors"
                            data-testid={`nav-service-${service.id}`}
                          >
                            {service.name}
                          </Link>
                        ))}
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>

              {/* Health Packages Dropdown */}
              <NavigationMenu>
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <NavigationMenuTrigger
                      className={`font-medium ${
                        location.pathname.startsWith('/health-packages') ? 'text-orange-800' : 'text-black-700'
                      }`}
                      data-testid="nav-health-packages"
                    >
                      Health Packages
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="w-64 p-4">
                        {healthPackagesData.map((pkg) => (
                          <Link
                            key={pkg.id}
                            to={`/health-packages/${pkg.id}`}
                            className="block px-4 py-3 rounded-lg hover:bg-orange-50 text-gray-700 hover:text-black-800 transition-colors"
                            data-testid={`nav-package-${pkg.id}`}
                          >
                            {pkg.name}
                          </Link>
                        ))}
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>

              <Link
                to="/endoscopy"
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  isActive('/endoscopy') ? 'text-black-800 bg-orange-50' : 'text-gray-700 hover:text-black-800 hover:bg-orange-50'
                }`}
                data-testid="nav-endoscopy"
              >
                Endoscopy
              </Link>

              {/* <Link
                to="/events"
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  isActive('/events') ? 'text-green-800 bg-green-50' : 'text-gray-700 hover:text-green-800 hover:bg-green-50'
                }`}
                data-testid="nav-events"
              >
                Events
              </Link> */}

              <Link
                to="/gallery"
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  isActive('/gallery') ? 'text-black-800 bg-orange-150' : 'text-gray-700 hover:text-black-800 hover:bg-orange-50'
                }`}
                data-testid="nav-gallery"
              >
                Gallery
              </Link>

              <Link
                to="/about"
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  isActive('/about') ? 'text-black-800 bg-orange-150' : 'text-gray-700 hover:text-black-800 hover:bg-orange-50'
                }`}
                data-testid="nav-about"
              >
                About Us
              </Link>

              {/* <Link
                to="/faqs"
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  isActive('/faqs') ? 'text-green-800 bg-green-50' : 'text-gray-700 hover:text-green-800 hover:bg-green-50'
                }`}
                data-testid="nav-faqs"
              >
                FAQs
              </Link> */}

              <Link
                to="/blogs"
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  isActive('/blogs') ? 'text-black-800 bg-orange-10' : 'text-gray-700 hover:text-black-800 hover:bg-orange-50'
                }`}
                data-testid="nav-blogs"
              >
                Blogs
              </Link>

              <Link
                to="/contact"
                className="ml-2 px-6 py-2 bg-green-800 text-white rounded-full font-medium hover:shadow-lg hover:scale-105 transition-all"
                data-testid="nav-contact"
              >
                Contact Us
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t" data-testid="mobile-menu">
              <nav className="flex flex-col gap-2">
                <Link to="/" className="px-4 py-3 rounded-lg text-gray-700 hover:bg-green-50 hover:text-green-800 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  Home
                </Link>
                <Link to="/services" className="px-4 py-3 rounded-lg text-gray-700 hover:bg-green-50 hover:text-green-800 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  Services
                </Link>
                <Link to="/health-packages" className="px-4 py-3 rounded-lg text-gray-700 hover:bg-green-50 hover:text-green-800 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  Health Packages
                </Link>
                <Link to="/endoscopy" className="px-4 py-3 rounded-lg text-gray-700 hover:bg-green-50 hover:text-green-800 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  Endoscopy
                </Link>
                <Link to="/events" className="px-4 py-3 rounded-lg text-gray-700 hover:bg-green-50 hover:text-green-800 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  Events
                </Link>
                <Link to="/gallery" className="px-4 py-3 rounded-lg text-gray-700 hover:bg-green-50 hover:text-green-800 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  Gallery
                </Link>
                <Link to="/about" className="px-4 py-3 rounded-lg text-gray-700 hover:bg-green-50 hover:text-green-800 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  About Us
                </Link>
                <Link to="/faqs" className="px-4 py-3 rounded-lg text-gray-700 hover:bg-green-50 hover:text-green-800 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  FAQs
                </Link>
                <Link to="/blogs" className="px-4 py-3 rounded-lg text-gray-700 hover:bg-green-50 hover:text-green-800 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  Blogs
                </Link>
                <Link to="/contact" className="mx-4 mt-2 px-6 py-3 bg-green-800 text-white rounded-full font-medium text-center" onClick={() => setMobileMenuOpen(false)}>
                  Contact Us
                </Link>
              </nav>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
